import {
  validateEmail,
  validateUSPhoneNumber,
} from '@linqpal/models/src/helpers/validations'
import { parsePhoneNumber } from 'libphonenumber-js'
import { IDisposer, Instance, types } from 'mobx-state-tree'
import { editableModel, exceptions, routes2, routes } from '@linqpal/models'
import moment from 'moment'
import RootStore from '../../../store'
import fb from '../../../utils/service/fb'
import { errorHolder } from '../../../store/errorholder'
import persistModel from '../../../store/persistable'
import validatePassword from '../validatePassword'
import makeInspectable from 'mobx-devtools-mst'
import cooker from '../../../utils/service/cooker'
import { callCustomEvent } from '../../../utils/helpers/callCustomEvent'
import { event_names } from '@linqpal/models/src/dictionaries/eventsNames'
import { currentZDate } from '@linqpal/models/src/helpers/date'

const errorTargets = ['login', 'verify', 'forgot']

function views(_self) {
  const self = _self as ISignInStoreModel
  return {
    get isValidLogin() {
      return self.isValidEmail || self.isValidPhone
    },
    get isValidEmail() {
      return validateEmail(self.login)
    },
    get isValidPhone() {
      return validateUSPhoneNumber(self.login)
    },
    get isValidPassword() {
      const check = validatePassword(self.password)
      return check.length && check.uppercase && check.number
    },
    get passwordValidation() {
      return validatePassword(self.password)
    },
    get canSubmit() {
      return (
        (self.isValidEmail && validatePassword(self.password).length) ||
        self.isValidPhone
      )
    },
    get phoneNumber() {
      try {
        const phoneNumber = parsePhoneNumber(self.login, 'US')
        return phoneNumber.number as string
      } catch {
        return ''
      }
    },
    get canAskOtp(): boolean {
      return !!self.codeVerifier
    },
    get isPasswordConfirmed() {
      return this.isValidPassword && self.password === self.passwordConfirm
    },
    get showVerifyPage() {
      return (
        (self.isValidPhone && self.codeVerifier !== null) ||
        (self.isValidEmail && self.canAskPassword)
      )
    },
  }
}

function actions(_self) {
  const self = _self as ISignInStoreModel
  const disposers = new Array<IDisposer>()
  const deferred = new Array<any>()
  return {
    afterCreate() {},
    beforeDestroy() {
      disposers.forEach((d) => d())
    },
    setLogin(value) {
      self.login = value
    },
    setIsNewUser(val) {
      self.isNewUser = val
    },
    setPassword(value) {
      self.password = value
    },
    setCanAskPassword(value) {
      self.canAskPassword = value
    },
    async checkIsExist() {
      RootStore.setBusy(true)
      try {
        const { exists } = await routes2.user.check({
          login: self.login.toLowerCase(),
        })
        if (!exists) {
          const code = self.isValidEmail
            ? 'user-email-not-found'
            : 'user-phone-not-found'
          throw new exceptions.LogicalError(code)
        }
        RootStore.setBusy(false)
        self.removeError('login')
        return true
      } catch (e: any) {
        self.setError('login', 'error', e.code)
        RootStore.setBusy(false)
        return false
      }
    },
    async signIn(verifier?) {
      if (!self.canSubmit) {
        const error = new exceptions.LogicalError('error/not-filled')
        self.setError('login', 'error', error.code)
        return Promise.reject(error)
      }
      self.removeError('login')

      try {
        self.checkLoginAttempts()
      } catch (error: any) {
        self.setError('login', 'error', error.code)
        return Promise.reject(error)
      }

      let promise
      if (self.isValidEmail) {
        promise = self.signInWithEmail()
      } else if (self.isValidPhone) {
        promise = self.signInWithPhone(verifier)
      }
      return promise
        .then((idToken) => routes2.user.webAuth({ idToken }))
        .then(({ session, challenge }) => {
          cooker.set('session', session, { maxAge: 60 * 60 })
          cooker.set('auth-challenge', challenge, {
            maxAge: 80 * 24 * 60 * 60,
          })
          self.setPassword('')
          routes.user.conversion({
            lastLogin: currentZDate(),
          })
          return RootStore.userStore.setAuthState(session)
        })
        .catch((error) => {
          const code =
            error.code === 'auth/user-not-found'
              ? self.isValidEmail
                ? 'auth/wrong-password'
                : 'auth/invalid-verification-code'
              : error.code
          self.setError('login', 'error', code)
          if (code === 'auth/user-not-found') {
            RootStore.userStore.logOut()
          }
          return Promise.reject(error)
        })
        .finally(() => RootStore.setBusy(false))
    },
    async signInWithPhone(verifier?) {
      return fb
        .auth()
        .signInWithPhoneNumber(self.phoneNumber, verifier)
        .then((codeVerifier) => {
          self.setCodeVerifier(codeVerifier)
          RootStore.setBusy(false)
          return new Promise((resolve, reject) => {
            deferred.unshift({ resolve, reject })
          })
        })
    },
    async newUserSignInVerify(code) {
      if (!self.codeVerifier) {
        return
      }
      RootStore.setBusy(true)
      self.removeError('verify')
      return (self.codeVerifier as unknown as fb.auth.ConfirmationResult)
        .confirm(code)
        .catch(self.onSingInError)
        .catch((error) => {
          self.setError('verify', 'error', error.code)
          return Promise.reject(error)
        })
        .finally(() => RootStore.setBusy(false))
    },
    async signInVerifyPhone(code) {
      if (!self.codeVerifier) {
        return
      }
      RootStore.setBusy(true)
      self.removeError('verify')
      return (self.codeVerifier as unknown as fb.auth.ConfirmationResult)
        .confirm(code)
        .catch(self.onSingInError)
        .then(({ user }) => user?.getIdToken())
        .then((idToken) => {
          callCustomEvent(event_names.signIn_with_phone)
          deferred[0].resolve(idToken)
        })
        .catch((error) => {
          console.log('code verify', error)
          self.setError('verify', 'error', error.code)
          return Promise.reject(error)
        })
        .finally(() => RootStore.setBusy(false))
    },
    async signInWithEmail() {
      return fb
        .auth()
        .signInWithEmailAndPassword(self.login, self.password)
        .catch(self.onSingInError)
        .then((result) => {
          callCustomEvent(event_names.signIn_wih_email)
          return result.user?.getIdToken()
        })
    },
    setCodeVerifier(verifier) {
      self.removeError('verify')
      console.log('verifier', verifier)
      self.codeVerifier = verifier
    },
    async sendPasswordResetEmail() {
      self.removeError('forgot')
      RootStore.setBusy(true)
      if (!self.isValidLogin) {
        return self.setError('forgot', 'error', 'auth/invalid-email')
      }
      return fb
        .auth()
        .sendPasswordResetEmail(self.login)
        .catch((e) => {
          if (e.code !== 'auth/user-not-found') {
            self.setError('forgot', 'error', e.code)
            return Promise.reject(e)
          } else {
            return Promise.resolve()
          }
        })
        .finally(() => RootStore.setBusy(false))
    },
    async verifyResetCode(code) {
      return fb
        .auth()
        .verifyPasswordResetCode(code)
        .then((e) => self.setLogin(e))
        .catch((e) => self.setError('forgot', 'error', e.code))
    },
    async resetPassword(code) {
      self.removeError('forgot')
      if (!self.isPasswordConfirmed) {
        return self.setError('forgot', 'error', 'auth/password-not-valid')
      }
      return RootStore.logOut()
        .catch(() => Promise.resolve())
        .then(() => fb.auth().confirmPasswordReset(code, self.password))
        .catch((e) => self.setError('forgot', 'error', e.code))
    },
    checkLoginAttempts() {
      if (getLoginAttempts(self.login) < 5) return
      throw new exceptions.LogicalError('auth/too-many-requests')
    },
    async onSingInError(error) {
      if (!error?.code) throw error
      try {
        incrLoginAttempts(self.login)
      } catch {}
      throw error
    },
  }
}

function getLoginAttempts(login: string) {
  const name = encodeURIComponent(`login-attempts:${login}`)
  return parseInt(cooker.get(name) || '0') || 0
}

function incrLoginAttempts(login: string) {
  const name = encodeURIComponent(`login-attempts:${login}`)
  const attempts = getLoginAttempts(login) + 1
  cooker.set(name, attempts.toString(), {
    maxAge: moment().add(1, 'day').diff(moment(), 'seconds'),
  })
}

export const SignInStoreModel = types
  .compose(
    types
      .model({
        login: '',
      })
      .volatile(() => ({
        password: '',
        codeVerifier: null,
        passwordConfirm: '',
        canAskPassword: false,
        isNewUser: false,
      }))
      .views(views)
      .actions(actions),
    editableModel(),
    persistModel('BT_SIGNIN', true),
    errorHolder(errorTargets, false),
  )
  .named('SignInStore')
const store = SignInStoreModel.create(
  process.env.NODE_ENV === 'development'
    ? {
        login: '7025555555',
      }
    : {},
)
makeInspectable(store)

interface ISignInStoreModel extends Instance<typeof SignInStoreModel> {}

export default store
